import { inject, Injectable } from '@angular/core';
import { RestApiService } from '@core/services/rest-api.service';
import { map, Observable } from 'rxjs';
import {
  GetRouteNetworksInAreaReq,
  GetRouteNetworksInAreaRes,
  POIInAreaReq,
  POIInAreaResp,
  RouteNetwork,
  RouteNetworkMaintainer,
  RouterMaintainersResp,
} from '../interfaces/map-data';
import { MapDataHubService } from './map-data-hub.service';
import { VippsDonateReq, VippsDonateResp } from '@shared/interfaces/vipps';

@Injectable({
  providedIn: 'root',
})
export class MapDataService {
  private restApiService = inject(RestApiService);
  private mapDataHubService = inject(MapDataHubService);

  constructor() {
    this.mapDataHubService.startMapDataConnection();
  }

  getMapStateFromURL(): {
    lat: number;
    lng: number;
    zoom?: number;
    label?: string | null;
  } | null {
    const params = new URLSearchParams(window.location.search);

    const latStr = params.get('lat');
    const lngStr = params.get('lng');
    const zoomStr = params.get('zoom');
    const label = params.get('label');

    if (!latStr || !lngStr) {
      return null;
    }

    const lat = parseFloat(latStr);
    const lng = parseFloat(lngStr);
    const zoom = zoomStr ? parseInt(zoomStr, 10) : '';

    if (isNaN(lat) || isNaN(lng)) {
      return null;
    }

    if (zoom) {
      return { lat, lng, zoom, label };
    }

    return { lat, lng, label };
  }

  getRouteMaintainers(): Observable<RouterMaintainersResp> {
    return this.restApiService.get<RouterMaintainersResp>('Route/Maintainers');
  }

  getRouteNetworksInArea(
    payload: GetRouteNetworksInAreaReq,
    abortSignal: AbortSignal,
    rowVersion?: number,
  ): Observable<GetRouteNetworksInAreaRes> {
    return this.restApiService.post<GetRouteNetworksInAreaRes>(
      `Route/NetworksInArea/${rowVersion ? rowVersion : ''}`,
      payload,
      {
        signal: abortSignal,
      },
    );
  }

  getRouteNetworkById(id: number | string): Observable<Partial<RouteNetwork>> {
    return this.restApiService.post<Partial<RouteNetwork>>(`Route/getInfo/RouteNetworkWeb/${id}`, [
      'RouteNetworkID',
      'Description',
      'Name',
      'LastMaintainedTimeUTC',
      'Association',
      'CustomerID',
      'CountyName',
      'VippsEnabled',
      'Length',
      'Latitude',
      'Longitude',
      'RouteSegmentTypes',
      'RouteSegmentFeatures',
      'RouteNetworkMaintainerTypeID',
      'RouteNetworkMaintainerType_TranslationKey',
    ] satisfies (keyof RouteNetwork)[]);
  }

  getPOIInArea(payload: POIInAreaReq, abortSignal?: AbortSignal): Observable<POIInAreaResp> {
    return this.restApiService.post<POIInAreaResp>('Route/POISInArea', payload, {
      signal: abortSignal,
    });
  }

  setBoundsForRouteNetworkUpdates(payload: GetRouteNetworksInAreaReq | undefined): void {
    this.mapDataHubService.setCurrentMapArea(payload).then();
  }

  getRouteMaintainerUpdates(): Observable<RouteNetworkMaintainer[]> {
    return this.mapDataHubService.maintainers$.pipe(
      map((resp) => resp?.RouteNetworkMaintainers || []),
    );
  }

  getRouteNetworkUpdates(): Observable<RouteNetwork[]> {
    return this.mapDataHubService.networks$.pipe(map((resp) => resp?.RouteNetworks || []));
  }

  donate(routeNetworkId: number, amount: number, returnUrl: string): Observable<VippsDonateResp> {
    const payload: VippsDonateReq = {
      RouteNetworkID: routeNetworkId,
      Amount: amount,
      ReturnUrl: returnUrl,
    };
    return this.restApiService.post<VippsDonateResp>('route/web/donate', payload);
  }

  cancelDonation(refId: string): Observable<VippsDonateResp> {
    return this.restApiService.post<VippsDonateResp>(`route/donation/cancel/${refId}`);
  }
}
